package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Ul

@Page("/kmm/decompose")
@Composable
fun KMMDecomposePage() {
    BlogLayout(
        PageProperties(
            "Kotlin Multiplatform",
            ""),
        BlogProperties("Kotlin Multiplatform", "Decompose")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Span(Modifier.display(DisplayStyle.Contents).toAttrs()) {
                SpanText(
                    text = "Decompose is a Kotlin Multiplatform library for breaking down your code into tree-structured lifecycle-aware business logic components (aka BLoC), with routing functionality and pluggable UI (Jetpack/Multiplatform Compose, Android Views, SwiftUI, Kotlin/React, etc.).",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "Instance Keeper",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The first super power of our ComponentContext is its ability to persist resources across app events and also manage their own lifecycle in order to save and optimize memory.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The ComponentContext conforms to the InstanceKeeperOwner interface, which has a reference to a InstanceKeeper instance, who is responsible to keep the consistency of all the resource instances contained in the component and dispose them once the component is destroyed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Instance object implements a onDestroy function that is responsible of disposing the retained resource instances.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Instance object implements a onDestroy function that is responsible of disposing the retained resource instances.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The main thing on that is the inner interface that InstanceKeeper contains, which is the Instance , that has only one method: onDestroy. Basically this method is responsible for disposing all the resources that exist in the Component, and the instance keeper is able to manage multiple instances at the same time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel is creating an Instance object that will be persisted across ViewModel configuration changes and lifecycle events until the CustomViewModel is deleted from memory, which will call the onDestroy method and cancel the viewModelScope . If we didn't use it, probably we would not be able to free the viewModelScope , since the instance responsible for it would not be retained.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Lifecycle",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is the easiest concept since it's very similar to the methods we are used to deal with in activities and fragments in Kotlin. Basically the ComponentContext also implements the LifecycleOwner interface, which contains a Lifecycle type instance, and that allows us to provide implementation to lifecycle events, which are very close to the ones we implemented in an Activity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example we see that we subscribe an instance of Lifecycle.Callbacksin the lifecycle object , which implements all the component lifecycle methods as it was a delegate. This object should be saved in memory since we need to unsubscribe once the component is destroyed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "BackHandler",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ComponentContext as a BackHandlerOwner,contains an instance of BackHandler , which is similar to Lifecycle , but instead of lifecycle events, this object intercept back button presses in the component context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "StateKeeper",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ComponentContext also has a StateKeeper , which has a similar concept to the InstanceKeeper , but instead of managing resources it is responsible for maintaining State objects, which by definition should be Serializable. This allows us to persist the states without the need of a database or back end.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    @Serializable
                    data class CustomState(var string: String)

                    class CustomViewModel(
                        componentContext: ComponentContext
                    ) : ComponentContext by componentContext {

                        // Initial state is fetched from state_key and deserialized as CustomState
                        var state = stateKeeper.consume("state_key", strategy = CustomState.serializer())

                        init {
                            // State key registering
                            stateKeeper.register(
                                "state_key",
                                strategy = CustomState.serializer()
                            ) { state }
                        }
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "we use the consume function from state keeper to retrieve the saved state that is tied to a specific key and assign to the state property.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Once our ViewModel is instantiated, we register the \"state_key\" to hold any instance of CustomState .",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With this strategy, we can persist any serializable instance of our states across configuration changes and screen lifecycle events.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}

//https://medium.com/codandotv/kotlin-decompose-component-context-capabilities-in-depth-c70f25b33750